import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { authApi } from '../services/api'
import Button from '../components/common/Button'
import Input from '../components/common/Input'

/**
 * Reset Password — public page.
 *
 * Loaded via the link in the reset email: /reset-password?token=xxx
 *
 * Validates locally that the token query param is present (the server
 * does the real validation when submitting). Shows new + confirm password
 * fields, requires 8+ chars and match. On success we send the user to
 * /login with a toast.
 *
 * If the token in the URL is invalid/expired/used, the server returns an
 * error message which we surface verbatim — gives the user a clear next
 * step (request another link).
 */
export default function ResetPassword() {
    const navigate = useNavigate()
    const [params] = useSearchParams()
    const token = params.get('token') || ''

    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')
    const [submitting, setSubmitting] = useState(false)

    // No-token state — they came to this URL without a token. Could be a typo
    // in the email link, or they bookmarked it for some reason.
    if (!token) {
        return (
            <Shell>
                <div className="text-center space-y-4">
                    <h2 className="text-xl font-semibold text-ghasc-navy">Missing reset token</h2>
                    <p className="text-gray-600 text-sm">
                        This page should be opened from the link in your password reset email.
                        If you don't have one, you can request a new one.
                    </p>
                    <div className="pt-2 flex justify-center gap-2">
                        <Button variant="secondary" onClick={() => navigate('/login')}>
                            Back to login
                        </Button>
                        <Button onClick={() => navigate('/forgot-password')}>
                            Request reset link
                        </Button>
                    </div>
                </div>
            </Shell>
        )
    }

    async function handleSubmit(e) {
        e.preventDefault()

        if (password.length < 8) {
            toast.error('Password must be at least 8 characters')
            return
        }
        if (password !== confirm) {
            toast.error('Passwords don\'t match')
            return
        }

        setSubmitting(true)
        try {
            const response = await authApi.resetPassword(token, password)
            if (response.success) {
                toast.success('Password reset! Please log in.')
                navigate('/login')
            } else {
                toast.error(response.message || 'Could not reset password')
            }
        } catch {
            toast.error('Network error. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Shell>
            <form onSubmit={handleSubmit} className="space-y-4">
                <h2 className="text-xl font-semibold text-ghasc-navy">Set a new password</h2>
                <p className="text-sm text-gray-500">
                    Choose a new password for your GHASC Literature account.
                </p>

                <Input
                    id="reset-password"
                    label="New password (8+ characters)"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                    autoComplete="new-password"
                />

                <Input
                    id="reset-confirm"
                    label="Confirm new password"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                />

                <Button type="submit" loading={submitting} className="w-full">
                    Reset password
                </Button>

                <p className="text-center text-sm text-gray-500 pt-4">
                    <Link to="/login" className="text-ghasc-navy hover:underline">
                        Back to login
                    </Link>
                </p>
            </form>
        </Shell>
    )
}

function Shell({ children }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ghasc-navy-dark to-ghasc-navy px-4 py-8">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
                <div className="text-center mb-6">
                    <h1 className="text-2xl font-bold text-ghasc-navy">GHASC Literature</h1>
                </div>
                {children}
            </div>
        </div>
    )
}
